import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  COMPLAINT_TYPE_LABELS,
  COMPLAINT_STATUS_LABELS,
  type ComplaintStatus,
} from "@ckgo2/shared/types";
import { MessageSquareWarning } from "lucide-react";

interface Row {
  id: string;
  orderId: string | null;
  type: string;
  status: ComplaintStatus;
  title: string;
  compensationAmount: number | null;
  currency: string;
  dueDate: string | null;
  orderNumber: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  assignedToName: string | null;
  createdAt: string;
}

const STATUS_BADGE: Record<ComplaintStatus, string> = {
  open: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-gray-100 text-gray-600",
};

export default function ComplaintsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["complaints-all"],
    queryFn: () => api.get<{ data: Row[] }>("/api/admin/complaints?limit=100"),
  });
  const rows = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Complaints</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Customer complaints & compensation — tracked to resolution
        </p>
      </div>

      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center text-gray-500">
            <MessageSquareWarning className="mb-2 h-10 w-10 text-gray-300" />
            <p>No complaints</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                {["Complaint", "Order", "Customer", "Type", "Compensation", "Status"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {r.title}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {r.orderId ? (
                      <Link to={`/orders/${r.orderId}`} className="text-brand hover:underline">
                        {r.orderNumber ?? "Order"}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {[r.customerFirstName, r.customerLastName].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {COMPLAINT_TYPE_LABELS[r.type as keyof typeof COMPLAINT_TYPE_LABELS] ?? r.type}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {r.compensationAmount
                      ? `${new Intl.NumberFormat("en-US").format(r.compensationAmount)} ${r.currency}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded px-2 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[r.status]}`}
                    >
                      {COMPLAINT_STATUS_LABELS[r.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
